import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { AppHeaderBar } from "@/components/ui/app-header";
import Link from "@/components/DashboardLink";
import { BarChart3, Bell, Github, House, SettingsIcon } from "lucide-react";
import { isCodesmithPath, isHomePath, isProofPath, isStandaloneAccountPath } from "./sidebar/useActiveApp";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import SidebarSkeleton from "./sidebar/SidebarSkeleton";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { SessionSkeleton } from "./codesmith/SessionSkeleton";
import { BlacksmithLogomarkAnimationSVG, BlacksmithLogomarkSVG } from "./BlacksmithLogoSVG";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { RAIL_REDESIGN_CLASS, RAIL_ROW, RAIL_ROW_ACTIVE } from "./sidebar/SidebarGlobalChrome";
import { readCachedSidebarVariant } from "./codesmith/sidebarVariant";
import { RailStatusWidgetSkeleton } from "./sidebar/StatusIndicator";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { cn } from "@repo/blacksmith-ui/cn";
import { RAIL_REDESIGN_WIDTH, RAIL_WIDTH } from "./sidebar/railConstants";
import { DesktopLayoutSkeleton } from "./desktop/DesktopLayoutSkeleton";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { getCachedCodesmithEnabled, getCachedLastActiveOrg } from "./sidebar/useCodesmithEnabledCache";

// This ensures the skeleton matches the actual sidebar state even during initial render
export function getSidebarStateFromStorage(): boolean {
  if (typeof window === "undefined") {
    return true; // Default to expanded on SSR
  }
  try {
    const stored = localStorage.getItem("sidebar-open");
    if (stored === null) {
      return true; // Default to expanded if not set
    }
    return stored === "true";
  } catch {
    return true; // Default to expanded on error
  }
}

/**
 * Twin of the mounted rail's nav row: same 32px footprint, icon box, and
 * resting icon color. With an href it is a live Link with the rail's hover
 * and focus affordances, so a click landing during the skeleton window
 * navigates instead of dying on placeholder chrome; without one it stays a
 * plain div that shouldn't advertise interactivity.
 */
function RailSkeletonRow(t0) {
  const $ = _c(15);
  const {
    icon,
    active,
    slot,
    href
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="rail-row-icon flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const contents = t1;
  if (href) {
    const t2 = active && RAIL_ROW_ACTIVE;
    let t3;
    if ($[2] !== t2) {
      t3 = cn(RAIL_ROW, t2);
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== contents || $[5] !== href || $[6] !== slot || $[7] !== t3) {
      t4 = <Link href={href} data-slot={slot} className={t3}>{contents}</Link>;
      $[4] = contents;
      $[5] = href;
      $[6] = slot;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const t2 = active && RAIL_ROW_ACTIVE;
  let t3;
  if ($[9] !== t2) {
    t3 = cn("rail-row flex h-8 w-full items-center overflow-hidden rounded-md", "[&_svg]:text-neutral-600 dark:[&_svg]:text-neutral-400", t2);
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== contents || $[12] !== slot || $[13] !== t3) {
    t4 = <div data-slot={slot} className={t3}>{contents}</div>;
    $[11] = contents;
    $[12] = slot;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}

/**
 * Placeholder matching AppSwitcherRail's slim footprint: logo, org-switcher
 * pill, app rows, and the global footer (three icon rows + status widget), so
 * the rail never sits as a blank strip while user/org data loads. Every fixed
 * glyph is the mounted rail's own icon rather than a gray block: none of them
 * depend on the data being awaited, so a placeholder there would only flash
 * before the identical icon lands. Only the org pill, whose content is the
 * data being loaded, stays a `Skeleton`; the status widget is a static twin at
 * its own pending values (see RailStatusWidgetSkeleton), which keeps the footer
 * rows above it at their final height. The Codesmith and Proof app rows follow
 * the same gate the mounted rail uses, so nothing pops in or out on swap.
 */
export function RailSkeleton() {
  const $ = _c(52);
  const router = useRouter();
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const {
    user
  } = useAuth();
  const urlInstanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  let activeSection;
  let navOrg;
  let redesign;
  let showCodesmithRow;
  let t0;
  if ($[0] !== router.pathname || $[1] !== urlInstanceSlug || $[2] !== urlOrg) {
    const org = urlOrg ? {
      orgName: urlOrg,
      instanceSlug: urlInstanceSlug
    } : getCachedLastActiveOrg(urlInstanceSlug);
    const isCodesmithRoute = isCodesmithPath(router.pathname);
    showCodesmithRow = getCachedCodesmithEnabled(org) !== false;
    const t1 = urlOrg ?? null;
    let t2;
    if ($[8] !== t1) {
      t2 = readCachedSidebarVariant(t1);
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    redesign = t2 === "redesign";
    navOrg = org?.orgName;
    activeSection = router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isHomePath(router.pathname) ? "home" : isStandaloneAccountPath(router.pathname) ? null : isCodesmithRoute ? "codesmith" : isProofPath(router.pathname) ? "proof" : "actions";
    t0 = cn("h-full shrink-0 bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", redesign && RAIL_REDESIGN_CLASS, isCodesmithRoute && "max-md:hidden");
    $[0] = router.pathname;
    $[1] = urlInstanceSlug;
    $[2] = urlOrg;
    $[3] = activeSection;
    $[4] = navOrg;
    $[5] = redesign;
    $[6] = showCodesmithRow;
    $[7] = t0;
  } else {
    activeSection = $[3];
    navOrg = $[4];
    redesign = $[5];
    showCodesmithRow = $[6];
    t0 = $[7];
  }
  const t1 = redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH;
  let t2;
  if ($[10] !== t1) {
    t2 = {
      width: t1
    };
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AppHeaderBar><div className="flex size-8 shrink-0 items-center justify-center"><div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div></div></AppHeaderBar>;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = redesign ? undefined : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-8 w-full rounded-lg" />;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4) {
    t6 = <div className={t4}>{t5}</div>;
    $[14] = t4;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = !redesign && "gap-1 pt-1";
  let t8;
  if ($[16] !== t7) {
    t8 = cn("rail-rows flex flex-col", t7);
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <House className="size-[18px]" />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = activeSection === "home";
  const t11 = navOrg ? `/${navOrg}/home` : undefined;
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <RailSkeletonRow icon={t9} active={t10} href={t11} />;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Github className="size-[18px]" />;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = activeSection === "actions";
  const t15 = navOrg ? `/${navOrg}/runs/jobs` : undefined;
  let t16;
  if ($[23] !== t14 || $[24] !== t15) {
    t16 = <RailSkeletonRow icon={t13} active={t14} href={t15} />;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== activeSection || $[27] !== navOrg || $[28] !== showCodesmithRow) {
    t17 = showCodesmithRow && <RailSkeletonRow icon={<CodesmithIcon />} active={activeSection === "codesmith"} slot="rail-skeleton-codesmith-row" href={navOrg ? `/${navOrg}/sessions/new` : undefined} />;
    $[26] = activeSection;
    $[27] = navOrg;
    $[28] = showCodesmithRow;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== activeSection || $[31] !== navOrg || $[32] !== showCodesmithRow) {
    t18 = showCodesmithRow && <RailSkeletonRow icon={<ProofIconBold className="size-[18px]" />} active={activeSection === "proof"} href={navOrg ? `/${navOrg}/proof` : undefined} />;
    $[30] = activeSection;
    $[31] = navOrg;
    $[32] = showCodesmithRow;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t12 || $[35] !== t16 || $[36] !== t17 || $[37] !== t18 || $[38] !== t8) {
    t19 = <div className={t8}>{t12}{t16}{t17}{t18}</div>;
    $[34] = t12;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t8;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="flex-1" />;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== activeSection || $[42] !== redesign) {
    t21 = redesign ? <div className="rail-rows flex flex-col pb-2"><RailSkeletonRow icon={<BarChart3 className="size-[18px]" />} active={activeSection === "usage"} /><RailSkeletonRow icon={<Bell className="size-[18px]" />} /><RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} /><RailStatusWidgetSkeleton /></div> : <div className="rail-rows flex flex-col gap-1 pb-2"><RailSkeletonRow icon={<BarChart3 className="size-[18px]" />} active={activeSection === "usage"} /><RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} /><RailSkeletonRow icon={<Bell className="size-[18px]" />} /><div className="pt-1"><RailStatusWidgetSkeleton /></div></div>;
    $[41] = activeSection;
    $[42] = redesign;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t19 || $[45] !== t21 || $[46] !== t6) {
    t22 = <div className="flex h-full w-full flex-col px-2">{t3}{t6}{t19}{t20}{t21}</div>;
    $[44] = t19;
    $[45] = t21;
    $[46] = t6;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t0 || $[49] !== t2 || $[50] !== t22) {
    t23 = <div className={t0} style={t2} data-slot="app-switcher-rail-skeleton">{t22}</div>;
    $[48] = t0;
    $[49] = t2;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  return t23;
}

/**
 * Full-page loading skeleton: rail + sidebar skeleton + centered logo in main
 * area, or rail + SessionSkeleton on codesmith routes (which have no Actions
 * sidebar; the session sidebar lives inside the main pane at a persisted width).
 * Used during auth checks and pending-auth query failures so loading matches app chrome.
 * AuthProvider only renders this on the client after hydration, so
 * localStorage can be read synchronously on first paint.
 */
export function SidebarLayoutSkeleton() {
  const $ = _c(19);
  const router = useRouter();
  const surface = useCodesmithSurface(router.query.embed);
  const [sidebarOpen] = useState(getSidebarStateFromStorage);
  let t0;
  if ($[0] !== router.pathname) {
    t0 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isCodesmithRoute = t0;
  let t1;
  if ($[2] !== router.pathname) {
    t1 = isStandaloneAccountPath(router.pathname);
    $[2] = router.pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isStandalone = t1;
  if (surface === "desktop") {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <DesktopLayoutSkeleton />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RailSkeleton />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isCodesmithRoute || $[7] !== isStandalone || $[8] !== router.pathname || $[9] !== sidebarOpen) {
    t3 = !isCodesmithRoute && !isHomePath(router.pathname) && !isProofPath(router.pathname) && <SidebarSkeleton collapsed={!sidebarOpen} standalone={isStandalone} />;
    $[6] = isCodesmithRoute;
    $[7] = isStandalone;
    $[8] = router.pathname;
    $[9] = sidebarOpen;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== isCodesmithRoute) {
    t4 = <SidebarInset className="flex-1 bg-transparent! overflow-y-auto [scrollbar-gutter:stable]">{isCodesmithRoute ? <SessionSkeleton /> : <div className="flex h-full items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>}</SidebarInset>;
    $[11] = isCodesmithRoute;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className="flex-1 flex overflow-hidden">{t2}{t3}{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== sidebarOpen || $[17] !== t5) {
    t6 = <div className="flex flex-col h-dvh"><SidebarProvider open={sidebarOpen} className="flex-1 min-h-0">{t5}</SidebarProvider></div>;
    $[16] = sidebarOpen;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}